import React, { FC } from 'react'
import {
  CartType,
  useRouter,
  orderCenterRoutes,
  CartView,
  cartRoute,
} from '@src/routes'
import {
  useGlobalSnackbar,
  SnackbarType,
} from '../GlobalSnackbar/globalSnackBarContext'
import { FormattedMessage } from 'react-intl'
import messages from '@utils/messages'
import ResponsiveModal, {
  ModalSizes,
} from '@src/components/ResponsiveModal/ResponsiveModal'
import { Button, Theme, Typography } from '@material-ui/core'
import { sendSessionErrorEvent } from '@utils/analytics'
import { handleGARemoveFromCart } from '@utils/analytics/cartAndCheckoutEvents'
import { useCancelCart, useDeleteParallelCart } from '@utils/useCart'
import { hasSessionExpired } from '@utils/errorHandler'
import { isBlueCartFE, getCartMiniAction } from '@utils/cartUtils'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { CartItem } from '@src/types/graphql-types'
import { QuickCartItemFragment } from '@src/queries/AllCartsQuery.generated'
import { CartItemFragment } from '@src/queries/CartQuery.generated'
import { sendRemoveItemsFromCartEvent } from '@utils/tealiumAnalytics'
import { EventValues } from '@sial/common-utils'
import vrStyles from '@src/styles/utils/vrStyles'
import { useRefetchSpecificFullCart } from '@utils/useRefetchSpecificFullCart'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { ErrorResponse } from '@apollo/client/link/error'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'

const { vr4 } = vrStyles
const useStyles = makeStyles((theme: Theme) => ({
  trigger: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(5),
    paddingTop: theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8),
      paddingTop: theme.spacing(5),
    },
  },
  main: {
    ...vr4,
    flexGrow: 1,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '16px',
    marginTop: 20,
    [theme.breakpoints.up('md')]: {
      gap: '10px',
      justifyContent: 'flex-end',
      flexDirection: 'row',
    },
  },
  item: {
    display: 'flex',
    flexDirection: 'row',
    fontSize: '16px',
  },
  cancelButton: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
}))

interface ClearCartItemsDialogProps {
  open: boolean
  onClose: (isConfirm: boolean) => void
  cartType?: CartType
  items?: CartItem[] | QuickCartItemFragment[] | CartItemFragment[] | null
  cartPage?: string
  marketplaceCart?: boolean | null
  currency?: string
}

const ClearCartItemsDialog: FC<ClearCartItemsDialogProps> = ({
  open,
  onClose,
  cartType = CartType.Active,
  items,
  cartPage = EventValues.ShoppingCart,
  marketplaceCart,
  currency,
}) => {
  const classes = useStyles()
  const router = useRouter()
  const {
    isBlueErpIntegrationEnabled,
    isB2BUser,
    userErpType,
    hasOnlyBlueERP,
  } = useCurrentUser()
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const [cancelCart] = useCancelCart({
    variables: {
      action: getCartMiniAction(cartType),
    },
    awaitRefetchQueries: true,
  })
  const [deleteParallelCart] = useDeleteParallelCart()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const refetchSpecificFullCart = useRefetchSpecificFullCart(cartType)

  const handleCancel = () => {
    onClose(false)
  }
  const handleConfirm = async () => {
    try {
      if (
        cartType === CartType.Active ||
        cartType === CartType.MarketplaceCart ||
        cartType === CartType.BlueCart
      ) {
        await cancelCart()
        refetchSpecificFullCart()
      } else {
        await deleteParallelCart({
          variables: { action: getCartMiniAction(cartType) },
        })
        refetchSpecificFullCart()

        setGlobalSnackbarState({
          open: true,
          message: <FormattedMessage {...messages.CART_DELETED} />,
          variant: SnackbarType.Success,
        })

        switch (cartType) {
          case CartType.SavedCart:
          case CartType.BlueSavedCart:
            router.push(orderCenterRoutes.savedCarts())
            break
          case CartType.DealerCart:
            router.push(orderCenterRoutes.dealerRequests())
            break
          default:
            router.push(orderCenterRoutes.index())
            break
        }
      }

      if (items) {
        const userType = { isBlueErpIntegrationEnabled, isB2BUser, userErpType }
        sendRemoveItemsFromCartEvent(items, userType)
        handleGARemoveFromCart(
          items,
          cartPage,
          marketplaceCart ? 'marketplacecart' : undefined,
          currency
        )
      }
      onClose(true)
      // after deleting marketplaceCart, blueCart, redirect to active cart
      if (
        !hasOnlyBlueERP &&
        router.query.action === CartView.Cart &&
        ((marketplaceCart && router.query.type === CartType.MarketplaceCart) ||
          router.query.type === CartType.BlueCart)
      ) {
        router.push(cartRoute[CartView.Cart]())
      }
    } catch (_err) {
      const errorMsg = hasSessionExpired(_err as ErrorResponse) ? (
        <FormattedMessage {...messages.USER_NOT_AUTHORIZED_OR_TOKEN_EXPIRED} />
      ) : (
        <FormattedMessage {...messages.GENERIC_ERROR} />
      )

      if (hasSessionExpired(_err as ErrorResponse)) {
        sendSessionErrorEvent()
      }

      setGlobalSnackbarState({
        open: true,
        message: errorMsg,
        variant: SnackbarType.Error,
      })
    }
  }

  return (
    <ResponsiveModal
      size={ModalSizes.Small}
      open={open}
      onClose={handleCancel}
      renderTitle={() => (
        <Typography variant="h2" component="h2">
          <FormattedMessage {...messages.DELETE_SHOPPING_CART} />
        </Typography>
      )}
    >
      <div className={classes.modalBody}>
        <div className={classes.main}>
          <div className={classes.item}>
            {marketplaceCart ? (
              <FormattedMessage
                {...messages.CANCEL_MARKETPLACE_CART_MODAL_DESCRIPTION}
              />
            ) : isBlueCartFE(cartType) && isChinaUser ? (
              <FormattedMessage
                {...messages.CANCEL_MERCK_CHEMICALS_CART_MODAL_DESCRIPTION}
              />
            ) : (
              <FormattedMessage {...messages.CANCEL_CART_MODAL_DESCRIPTION} />
            )}
          </div>
        </div>

        <div className={classes.modalActions}>
          <Button
            className={classes.cancelButton}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCancel}
          >
            <FormattedMessage {...messages.CANCEL} />
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleConfirm}
          >
            <FormattedMessage {...messages.AGREE} />
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  )
}

export default ClearCartItemsDialog
